import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../http';
import StationPinVerify from './StationPinVerify';
import StationView from './StationView';
import './ProctorApp.css';

import OfflineQueue from './OfflineQueue';
import GlobalLoginModal from '../GlobalLoginModal';
import { useAuth } from '../context/AuthContext';

export default function ProctorApp() {
  const [user, setUser] = useState(null);
  const [testEvent, setTestEvent] = useState(null);
  const [station, setStation] = useState(null);
  const [session, setSession] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingOps, setPendingOps] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [queueUnavailable, setQueueUnavailable] = useState(false);
  const syncingRef = useRef(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  // Counter incremented on every WebSocket event or polling tick.
  // StationView watches this to know when to re-fetch data.
  const [refreshSignal, setRefreshSignal] = useState(0);
  // Ref so the checkAuth catch can read this synchronously — state updates are async
  // and navigate would fire before React re-renders the modal.
  const loginModalActiveRef = React.useRef(false);
  // Distinguishes "session expired" (show modal) from "never authed" (go to /proctor/login).
  const wasAuthedRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionExpiredRole, clearSessionExpired } = useAuth();

  // Mirror context state into the synchronous ref so the checkAuth catch can read it.
  useEffect(() => {
    if (sessionExpiredRole === 'proctor' && wasAuthedRef.current) {
      loginModalActiveRef.current = true;
    }
  }, [sessionExpiredRole]);

  // PWA install prompt
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    const installed = () => {
      setInstallPrompt(null);
      setShowInstallBanner(false);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      setShowInstallBanner(false);
    }
  };


  // Check auth and load user. Exposed as a stable callback so the WebSocket
  // and polling effects can re-fetch the proctor's assignment without
  // duplicating the redirect logic.
  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/api/proctor/me');
      setUser(res.data.user);
      setStation(res.data.assignedStation || null);
      wasAuthedRef.current = true;
      if (!res.data.stationVerified &&
          location.pathname !== '/proctor/login' &&
          location.pathname !== '/proctor/verify-station') {
        navigate('/proctor/verify-station');
      }
    } catch (err) {
      // Two distinct cases:
      //  - Never authed in this tab → fresh visit; route to the login page.
      //  - Authed previously → http.js dispatched auth_unauthorized which
      //    flipped loginModalActiveRef synchronously; let the modal handle re-auth.
      if (!wasAuthedRef.current && location.pathname !== '/proctor/login') {
        navigate('/proctor/login');
      }
    }
  }, [navigate, location.pathname]);

  // Always-current ref to checkAuth so long-lived listeners (sockets, intervals)
  // call the latest version without re-subscribing on every render.
  const checkAuthRef = useRef(checkAuth);
  useEffect(() => { checkAuthRef.current = checkAuth; }, [checkAuth]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Socket connection — opens once per authed user (not gated on having a
  // station yet), so a brand-new assignment can push down without a refresh.
  // Station-room membership is managed by a separate effect below so we don't
  // tear down the socket on every reassignment.
  const socketRef = useRef(null);
  useEffect(() => {
    if (!user?.id) return;

    const socket = io(process.env.NODE_ENV === 'production' ? '/' : 'http://localhost:3000', {
      withCredentials: true
    });
    socketRef.current = socket;

    const refresh = () => setRefreshSignal(n => n + 1);

    socket.on('connect', () => {
      socket.emit('join_proctor', user.id);
      if (station?.id) socket.emit('join_station', station.id);
      refresh(); // Catch up on anything missed while reconnecting
    });
    socket.on('company:assigned', refresh);
    // Reassignment changes which station the proctor is bound to, so refetch
    // /api/proctor/me — which updates parent state and triggers the
    // verify-station redirect for the new station.
    socket.on('proctor:reassigned', () => { checkAuthRef.current(); });
    socket.on('session:status', refresh);
    socket.on('skill:result', refresh);

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-join the station room whenever the proctor's station changes, without
  // tearing down the socket. (No leave is needed: stale rooms simply stop
  // receiving traffic for this proctor once the server stops emitting to them.)
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !station?.id) return;
    if (socket.connected) socket.emit('join_station', station.id);
  }, [station?.id]);

  // Polling fallback — ensures tablet catches up even if a WebSocket event is missed.
  // Calls checkAuth so a missed proctor:reassigned still updates the parent state.
  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => {
      checkAuthRef.current();
      setRefreshSignal(n => n + 1);
    }, 15000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Online/offline detection + Trigger Sync
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      triggerSync();
    };
    const handleOffline = () => setIsOnline(false);
    const handleQueueUnavailable = () => setQueueUnavailable(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('queue_unavailable', handleQueueUnavailable);

    // Initial check on load
    if (navigator.onLine) {
      triggerSync();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('queue_unavailable', handleQueueUnavailable);
    };
  }, []);

  // Sync Logic
  const triggerSync = async () => {
    const count = await OfflineQueue.getCount();
    setPendingOps(count);

    if (count > 0 && navigator.onLine && !syncingRef.current) {
      syncingRef.current = true;
      setSyncing(true);
      try {
        const ops = await OfflineQueue.getAll();

        const res = await api.post('/api/proctor/sync', { operations: ops });

        if (res.data.success) {
          // Process results
          for (const result of res.data.results) {
            // Remove if successfully processed or already deduplicated (idempotent 409)
            if (result.statusCode < 400 || result.data?.code === 'idempotent') {
              await OfflineQueue.remove(result.opId);
            }
          }
        }
      } catch (err) {
        // Sync failed transiently — queue will retry on next poll
      } finally {
        syncingRef.current = false;
        setSyncing(false);
        const nextCount = await OfflineQueue.getCount();
        setPendingOps(nextCount);
      }
    }
  };

  // Poll for queue changes (for cases where http.js captures something)
  useEffect(() => {
    const interval = setInterval(async () => {
      const count = await OfflineQueue.getCount();
      if (count !== pendingOps) {
        setPendingOps(count);
        if (count > 0 && isOnline) triggerSync();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [pendingOps, isOnline]);

  // PWA install banner
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !window.matchMedia('(display-mode: standalone)').matches;

  const InstallBanner = () => {
    if (!showInstallBanner && !isIos) return null;
    return (
      <div className="install-banner" role="status">
        <span className="install-banner-icon" aria-hidden="true">📲</span>
        {isIos ? (
          <span className="install-banner-text">
            Tap <strong>Share</strong> then <strong>Add to Home Screen</strong> for the best experience
          </span>
        ) : (
          <span className="install-banner-text">Install this app for offline use and faster access</span>
        )}
        <div className="install-banner-actions">
          {!isIos && (
            <button className="install-banner-btn" onClick={handleInstall}>Install</button>
          )}
          <button className="install-banner-dismiss" onClick={() => setShowInstallBanner(false)} aria-label="Dismiss">✕</button>
        </div>
      </div>
    );
  };

  // Connection status banner
  const ConnectionBanner = () => {
    if (queueUnavailable) {
      return (
        <div className="connection-banner offline">
          <span>⚠️ Offline storage unavailable — actions will not be saved if connection is lost. Use a non-private browser window.</span>
        </div>
      );
    }
    if (isOnline && pendingOps === 0 && !syncing) return null;
    return (
      <div
        className={`connection-banner ${!isOnline ? 'offline' : syncing ? 'syncing' : 'pending'}`}
        role="status"
        aria-live="polite"
      >
        {!isOnline && (
          <span><span aria-hidden="true">📵 </span>Offline Mode</span>
        )}
        {isOnline && syncing && (
          <span><span aria-hidden="true">🔄 </span>Syncing {pendingOps} changes...</span>
        )}
        {isOnline && !syncing && pendingOps > 0 && (
          <span><span aria-hidden="true">⏳ </span>{pendingOps} actions pending sync</span>
        )}
      </div>
    );
  };

  // Bottom navigation
  const BottomNav = () => {
    if (!station) return null;
    const path = location.pathname;
    const items = [
      { route: '/proctor/queue',    match: '/queue',    icon: '👥', label: 'Queue' },
      { route: '/proctor/testing',  match: '/testing',  icon: '📋', label: 'Testing' },
      { route: '/proctor/results',  match: '/results',  icon: '📊', label: 'Results' },
      { route: '/proctor/settings', match: '/settings', icon: '⚙️', label: 'Settings' }
    ];
    return (
      <nav className="bottom-nav" aria-label="Proctor sections">
        {items.map((item) => {
          const isActive = path.includes(item.match);
          return (
            <button
              key={item.route}
              className={isActive ? 'active' : ''}
              aria-current={isActive ? 'page' : undefined}
              aria-label={item.label}
              onClick={() => navigate(item.route)}
            >
              <span className="nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          );
        })}
      </nav>
    );
  };

  return (
    <div className="proctor-app">
      {sessionExpiredRole === 'proctor' && wasAuthedRef.current && (
        <GlobalLoginModal
          role="proctor"
          onLoginSuccess={(userData) => {
            loginModalActiveRef.current = false;
            wasAuthedRef.current = true;
            setUser(userData);
            clearSessionExpired();
            triggerSync(); // Immediately flush any pending operations
          }}
        />
      )}
      <InstallBanner />
      <ConnectionBanner />

      <Routes>
        <Route path="/login" element={<ProctorLogin onLogin={setUser} />} />
        <Route
          path="/verify-station"
          element={
            <StationPinVerify
              user={user}
              assignedStation={station}
              onVerify={(data) => {
                setStation({ ...station, ...data });
                navigate('/proctor/queue');
              }}
            />
          }
        />
        <Route
          path="/queue"
          element={
            <StationView
              station={station}
              testEvent={testEvent}
              session={session}
              setSession={setSession}
              view="queue"
              refreshSignal={refreshSignal}
            />
          }
        />
        <Route
          path="/testing"
          element={
            <StationView
              station={station}
              testEvent={testEvent}
              session={session}
              setSession={setSession}
              view="testing"
              refreshSignal={refreshSignal}
            />
          }
        />
        <Route
          path="/results"
          element={
            <StationView
              station={station}
              testEvent={testEvent}
              session={session}
              setSession={setSession}
              view="results"
              refreshSignal={refreshSignal}
            />
          }
        />
        <Route
          path="/settings"
          element={
            <ProctorSettings
              user={user}
              station={station}
              onLogout={() => {
                wasAuthedRef.current = false;
                setUser(null);
                setStation(null);
              }}
              installPrompt={installPrompt}
              onInstall={handleInstall}
            />
          }
        />
        <Route
          path="*"
          element={
            <div className="empty-state">
              <h2>Page Not Found</h2>
              <button onClick={() => navigate('/proctor/queue')}>Go to Dashboard</button>
            </div>
          }
        />
      </Routes>

      <BottomNav />
    </div>
  );
}

// Simple login component
function ProctorLogin({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/proctor/login', { username, password });
      if (res.data.success) {
        onLogin(res.data.user);
        navigate('/proctor/verify-station');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="proctor-login">
      <div className="login-hero">
        <img src="/hero-portrait.jpg" alt="" className="login-hero-img" />
        <div className="login-hero-overlay" />
      </div>

      <div className="login-panel">
        <div className="login-panel-inner">
          <div className="login-brand">
            <span className="login-brand-name">EVOLUTION</span>
            <span className="login-brand-sub">Firefighter Practical Skills Tracking</span>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              <label htmlFor="login-username">Username</label>
              <input
                id="login-username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
              />
            </div>
            <div className="login-field">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            {error && <div className="login-error">{error}</div>}

            <button className="login-submit" type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Settings component
function ProctorSettings({ user, station, onLogout, installPrompt, onInstall }) {
  const navigate = useNavigate();
  const [queuedOps, setQueuedOps] = useState(0);
  const appVersion = process.env.REACT_APP_VERSION || 'dev';

  // Live offline-queue size — replaces a previous use of localStorage.length,
  // which counted every key on the origin (analytics, feature flags, etc.) and
  // wildly overstated the number of pending sync items.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const count = await OfflineQueue.getCount();
      if (!cancelled) setQueuedOps(count);
    };
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/api/proctor/logout');
    } catch (e) { }
    onLogout();
    navigate('/proctor/login');
  };

  const handleChangeStation = () => {
    navigate('/proctor/verify-station');
  };

  return (
    <div className="proctor-settings">
      <header className="proctor-header">
        <h1>Settings</h1>
      </header>

      <div className="settings-content">
        <div className="settings-brand-lockup">
          <svg className="settings-flame" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 2C16 2 8 10 8 18C8 22.4 10.4 26.2 14 28.4C13.2 26.8 13 25 13.4 23.2C14.6 25 16.4 26.2 18 26C17 24 17.2 21.6 18.8 20C19.6 22 20.8 23.4 22 24C22 20 20 16.4 16 14C17.6 16.4 18 19.2 16.8 22C15.2 20 14.4 17.2 15.2 14.4C13.2 16.4 12 19.4 12.4 22.6C10.4 20.4 9.6 17.2 10.4 14C9.2 16 8.4 18.4 8.8 20.8C8.2 19.8 8 18.6 8 17.4C8 23.4 11.6 28.4 17 30C17.4 30.2 17.8 30.2 18.2 30.4C22.6 28.6 26 24.4 26 18C26 10 18 2 16 2Z" fill="#f97316"/>
            <path d="M16 6C16 6 10 13 10 19C10 22.4 11.8 25.4 14.6 27.2C14.2 25.8 14.2 24.2 14.8 22.8C15.8 24.4 17.2 25.4 18.6 25.2C17.8 23.4 18 21.2 19.4 19.8C20 21.6 21 22.8 22 23.4C22 19.8 20.2 16.6 17 14.8C18.4 16.8 18.6 19.2 17.6 21.6C16.2 19.8 15.6 17.4 16.2 15C14.4 16.8 13.4 19.4 13.8 22C12 20.2 11.4 17.4 12 15C11.2 16.8 10.6 18.8 11 21C10.6 20.2 10.4 19.2 10.4 18.2C10.4 23 13.6 27 18 28.4C21.6 26.8 24 23.2 24 19C24 13 18 6 16 6Z" fill="#122044"/>
          </svg>
          <div className="settings-brand-text">
            <span className="settings-brand-name">EVOLUTION</span>
            <span className="settings-brand-sub">Firefighter Practical Skills Tracking</span>
          </div>
        </div>

        <div className="settings-section">
          <h2>Current User</h2>
          <div className="settings-item">
            <span className="label">Name</span>
            <span className="value">
              {user?.first_name} {user?.last_name}
            </span>
          </div>
          <div className="settings-item">
            <span className="label">Username</span>
            <span className="value">{user?.username}</span>
          </div>
          <div className="settings-item">
            <span className="label">Role</span>
            <span className="value">{user?.role}</span>
          </div>
        </div>

        {station && (
          <div className="settings-section">
            <h2>Current Station</h2>
            <div className="settings-item">
              <span className="label">Station</span>
              <span className="value">
                #{station.station_number} - {station.name}
              </span>
            </div>
            <button className="settings-button" onClick={handleChangeStation}>
              Change Station
            </button>
          </div>
        )}

        {installPrompt && (
          <div className="settings-section">
            <h2>Install App</h2>
            <p className="settings-hint">Add this app to your home screen for offline use and faster access.</p>
            <button className="settings-button" onClick={onInstall}>
              Install App
            </button>
          </div>
        )}

        <div className="settings-section">
          <h2>App Info</h2>
          <div className="settings-item">
            <span className="label">Version</span>
            <span className="value">{appVersion}</span>
          </div>
          <div className="settings-item">
            <span className="label">Pending Sync</span>
            <span className="value">{queuedOps} {queuedOps === 1 ? 'action' : 'actions'}</span>
          </div>
        </div>

        <button className="logout-button" onClick={handleLogout}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
